import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateProjectVersionBody,
  CreateProjectVersionData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface CreateProjectVersionState
  extends MergeRequestState<CreateProjectVersionData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.createProjectVersion,
  isFetching: (state: ReduxState) =>
    state.server.createProjectVersion.isFetching,
  data: (state: ReduxState) => state.server.createProjectVersion.data,
  error: (state: ReduxState) => state.server.createProjectVersion.error,
};

const initialState: CreateProjectVersionState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/createProjectVersion';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      body,
      config,
    }: { body: CreateProjectVersionBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.createProjectVersion(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerCreateProjectVersion {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const createProjectVersion: ServerCreateProjectVersion = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
